import { Button, FormControl, MenuItem, Select } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import type { PropertyGroup } from '~/generated/graphql';
import { Dialog, DialogContentTitle, DialogTitle } from './components';
import { DialogActions, DialogContent } from './lib/styles';

const INITIAL_VALUES = { id: '' };

export interface AddPropertyGroupDialogProps {
  description?: string;
  onClose: () => void;
  onSubmit: (values: { id: string }) => void;
  open: boolean;
  propertyGroups?: ReadonlyArray<Pick<PropertyGroup, 'id' | 'name'>>;
}

export const AddPropertyGroupDialog = ({
  description,
  onClose,
  onSubmit,
  open,
  propertyGroups,
}: AddPropertyGroupDialogProps) => {
  const { t } = useTranslation();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_VALUES,
    onSubmit: (values, { resetForm, setValues }) => {
      onSubmit(values);
      resetForm();
      setValues(INITIAL_VALUES);
    },
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      id: string().required().label('Property Group'),
    }),
  });

  const handleClose = useCallback(() => {
    onClose();
    formik.resetForm();
    void formik.setValues(INITIAL_VALUES);
  }, [formik, onClose]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>
        {t('common.dialogs.add_property_group.title')}
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <DialogContentTitle id="add-property-group-description">{description}</DialogContentTitle>
          <FormControl fullWidth variant="outlined" sx={{ alignItems: 'center', marginY: 2 }}>
            <Select
              name="id"
              fullWidth
              disabled={formik.isSubmitting}
              displayEmpty
              inputProps={{
                'aria-label': t('common.dialogs.add_property_group.group_label'),
                'aria-describedby': description ? 'add-property-group-description' : undefined,
              }}
              onChange={formik.handleChange}
              value={formik.values.id}
            >
              <MenuItem value="" disabled>
                {t('common.dialogs.add_property_group.placeholder')}
              </MenuItem>
              {propertyGroups?.map((propertyGroup) => (
                <MenuItem key={propertyGroup.id} value={propertyGroup.id}>
                  {propertyGroup.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {t('common.dialogs.add_property_group.cancel')}
          </Button>
          <Button
            color="primary"
            disabled={!formik.isValid || formik.isSubmitting}
            type="submit"
            variant="contained"
          >
            {t('common.dialogs.add_property_group.add')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
